// Screen 24 · The catalog editor (one catalog item).
// Answers: how does a product the shop will build again get set up once, so every job after
// it is inputs only? Spencer's layout of 2026-10-07 (docs/catalog-editor-layout-options.html,
// option A): a reference rail on the left with every input and calculation as key · value,
// always in view while a formula is written; the work on the right at full width: Recipes,
// then the cards Route, BOM, Cut list and Children, each foldable. Edit inputs and Edit
// calculations swap the sides: the table opens wide, and what it needs to see sits in a rail
// beside it. Editing a verified recipe returns it to unverified.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { parse, type LibStandardDef } from '@zerogap/engine';
import { useCrumb } from '../frame/Frame';
import { deleteRow, insertRow, refresh, rpc, toast, updateRow, updateRows, useRows, type Row } from '../lib/api';
import { qty, plural, today } from '../lib/format';
import { useLibrary } from '../lib/library';
import { namesRead } from '../lib/reads';
import { useSession } from '../lib/session';
import { Btn, Edit, Empty, Field, Fold, Head, Info, Loading, Modal, Panel, Seg, Tag } from '../ui/kit';
import { PackFields, priceWords } from '../ui/pack';
import { FilterEditor, filterWords } from '../ui/materialfilter';
import { Grip, useDragRows } from '../ui/move';
import { ListEdit } from '../ui/settingvalue';
import { CatalogTree } from './CatalogTree';
import { ChangeNote, changesOn, Since, useLook, useVerificationHistories } from './CheckSheet';
import { MaterialPick } from './job/ItemTab';
import { useStandardsChange, VerifyButton } from './Library';
import { ChecklistLines } from './Refs';

/** A formula is kept only if the engine can read it. */
const formula = (t: string) => {
  if (t.trim() === '') return undefined;
  try {
    parse(t);
    return t.trim();
  } catch {
    return undefined;
  }
};
/** A formula that may be blank. */
const formulaOrBlank = (t: string) => (t.trim() === '' ? '' : formula(t));
const snake = (t: string) => (/^[a-z][a-z0-9_]*$/.test(t.trim()) ? t.trim() : undefined);

/**
 * The recipe names in use across the catalog, most used first, with how many items carry each.
 * This is the list a job standard picks from: there is no separate list to keep.
 */
export function recipeNamesInUse(recipes: Row[]): { name: string; items: number }[] {
  const by = new Map<string, Set<string>>();
  for (const r of recipes) {
    const name = String(r.name ?? '').trim();
    if (!name) continue;
    const k = name.toLowerCase();
    if (!by.has(k)) by.set(k, new Set());
    by.get(k)!.add(String(r.catalog_item_id));
  }
  const spelled = new Map<string, string>();
  for (const r of recipes) {
    const name = String(r.name ?? '').trim();
    if (name && !spelled.has(name.toLowerCase())) spelled.set(name.toLowerCase(), name);
  }
  return [...by.entries()].map(([k, set]) => ({ name: spelled.get(k)!, items: set.size })).sort((a, b) => b.items - a.items || a.name.localeCompare(b.name));
}

/** A new recipe for one item: its name, and whether it starts blank or as a copy of one of the item's recipes. */
function NewRecipe({ itemId, recipes, onClose, onMade }: { itemId: string; recipes: Row[]; onClose: () => void; onMade: (id: string) => void }) {
  const [name, setName] = useState('');
  const [from, setFrom] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const taken = recipes.some((r) => r.name.toLowerCase() === name.trim().toLowerCase());
  const make = async () => {
    if (!name.trim() || taken || busy) return;
    setBusy(true);
    try {
      const id = from ? await rpc<string>('copy_recipe', { p_id: from, p_name: name.trim() }) : (await insertRow('recipe', { catalog_item_id: itemId, name: name.trim(), is_default: recipes.length === 0 })).id;
      refresh('recipe', 'recipe_route_step', 'recipe_bom_line', 'catalog_part', 'catalog_item');
      onMade(id as string);
      onClose();
    } finally {
      setBusy(false);
    }
  };
  const source = recipes.find((r) => r.id === from);
  return (
    <Modal title="New recipe" onClose={onClose}>
      <div className="form">
        <Field label={<>Name<Info>Shop-built, Cabinotch, a vendor's name, or your own. A job standard such as Box build flips every item that has a recipe of the name it picks, so spell a shared name the same on every item.</Info></>}>
          <input
            className="cell"
            list="recipe-names"
            value={name}
            autoFocus
            placeholder="Recipe name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void make();
            }}
          />
          {taken && <div className="warn">This item already has a recipe of that name.</div>}
        </Field>
        <Field label={<>Start from<Info>A copy brings the recipe's route steps, BOM lines and parts, to change as you like. Blank starts with none.</Info></>}>
          <select className="cell" value={from} onChange={(e) => setFrom(e.target.value)}>
            <option value="">Blank</option>
            {recipes.map((r) => (
              <option key={r.id} value={r.id}>
                A copy of {r.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="row mid" style={{ gap: 6, marginTop: 10 }}>
        <Btn primary disabled={!name.trim() || taken || busy} onClick={() => void make()}>
          {source ? `Copy ${source.name} as ${name.trim() || '…'}` : 'Add recipe'}
        </Btn>
        <Btn onClick={onClose}>Cancel</Btn>
      </div>
      <small>The new recipe lands unverified{source ? `, and ${source.name} stays as it is` : ''}.</small>
    </Modal>
  );
}

// ---------- folding, kept for the session ----------

function foldKey(itemId: string, key: string) {
  return `zg.catalog.fold.${itemId}.${key}`;
}
function readFold(itemId: string, key: string, open: boolean): boolean {
  try {
    const v = sessionStorage.getItem(foldKey(itemId, key));
    return v === null ? open : v === '1';
  } catch {
    return open;
  }
}
function writeFold(itemId: string, key: string, open: boolean) {
  try {
    sessionStorage.setItem(foldKey(itemId, key), open ? '1' : '0');
  } catch {
    /* no session storage: the cards simply start as they do */
  }
}

/** A card that folds (the kit's Fold), each kept open or folded per item for the session. */
function Card({ itemId, k, label, right, open: openDefault = true, force, children, className }: { itemId: string; k: string; label: ReactNode; right?: ReactNode; open?: boolean; /** Opens the card whatever was remembered: the row a job screen sent the person to is inside it. */ force?: boolean; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(() => force || readFold(itemId, k, openDefault));
  useEffect(() => {
    if (force) setOpen(true);
  }, [force]);
  const toggle = () => {
    setOpen(!open);
    writeFold(itemId, k, !open);
  };
  return (
    <Fold open={open} onToggle={toggle} label={label} right={right} className={className}>
      {children}
    </Fold>
  );
}

/** One line of the reference rail: a key and what it is. */
function RailRow({ k, v, title }: { k: string; v?: ReactNode; title?: string }) {
  return (
    <div className="railrow" title={title}>
      <code>{k}</code>
      {v !== undefined && <span className="muted">{v}</span>}
    </div>
  );
}

/** What an input is, in a few words, for the rail. */
function inputWords(i: Row, def: LibStandardDef | undefined): string {
  if (i.data_type === 'standard') return def ? `→ ${def.key}` : `→ ${i.standard_key || i.key} (missing)`;
  if (i.data_type === 'material') return `material${filterWords(i.material_filter) ? ` · ${filterWords(i.material_filter)}` : ''}`;
  if (i.data_type === 'choice') return (Array.isArray(i.choices) ? i.choices : []).join(' | ') || 'choice';
  if (i.data_type === 'boolean') return 'yes / no';
  if (i.data_type === 'number') return [i.unit, i.default_value].filter((x) => x !== null && x !== undefined && x !== '').join(' · ') || 'number';
  return i.data_type as string;
}

export function RecipeEditor() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { can, setting } = useSession();
  const families = setting<string[]>('catalog_families', []);
  const item = useRows('catalog_item', { eq: { id } }).rows[0];
  // a row an AI employee retired (2026-10-23) stays in its table behind Show retired, with Restore, and is not part of the item the
  // engine prices (snapshot.ts leaves it out); the lists below show the live rows unless Show retired is on
  const [showRetired, setShowRetired] = useState(false);
  const live = <T extends { rows: Row[] }>(q: T): T => (showRetired ? q : { ...q, rows: q.rows.filter((x) => !x.retired_at) });
  const inputsAll = useRows('catalog_input', { eq: { catalog_item_id: id }, order: ['sort'] });
  const derivedAll = useRows('catalog_derived', { eq: { catalog_item_id: id }, order: ['sort'] });
  const childrenAll = useRows('catalog_child', { eq: { catalog_item_id: id }, order: ['sort', 'created_at'] });
  const recipes = useRows('recipe', { eq: { catalog_item_id: id }, order: ['created_at'] });
  const stepsAll = useRows('recipe_route_step', { in: { recipe_id: recipes.rows.map((r) => r.id) }, order: ['sort'] });
  const bomAll = useRows('recipe_bom_line', { in: { recipe_id: recipes.rows.map((r) => r.id) }, order: ['sort'] });
  const partsAll = useRows('catalog_part', { in: { recipe_id: recipes.rows.map((r) => r.id) }, order: ['sort', 'created_at'] });
  const inputs = live(inputsAll);
  const derived = live(derivedAll);
  const children = live(childrenAll);
  const steps = live(stepsAll);
  const bom = live(bomAll);
  const parts = live(partsAll);
  const retiredCount = [inputsAll, derivedAll, childrenAll, stepsAll, bomAll, partsAll].reduce((n, q) => n + q.rows.filter((x) => x.retired_at).length, 0);
  /** How many retired rows a card holds, said in its heading so the switch points at the card (Iris, 2026-10-23). */
  const retiredIn = (q: { rows: Row[] }, filter: (x: Row) => boolean = () => true) => q.rows.filter((x) => x.retired_at && filter(x)).length;
  const retiredWords = (n: number) => (n > 0 ? ` · ${n} retired` : '');
  const allRecipes = useRows('recipe');
  const namesInUse = useMemo(() => recipeNamesInUse(allRecipes.rows), [allRecipes.rows]);
  const [addingRecipe, setAddingRecipe] = useState(false);
  const ops = useRows('operation', { order: ['sort', 'name'] });
  const mats = useRows('material', { order: ['description'] });
  const cats = useRows('catalog_item', { order: ['name'] });
  const types = useRows('job_type', { order: ['name'] });
  const offers = useRows('job_type_catalog_item', { eq: { catalog_item_id: id } });
  const standards = useRows('standard_def', { order: ['sort'] });
  const usedBy = useRows('job_item', { eq: { catalog_item_id: id } });
  const createdBy = useRows('catalog_child', { eq: { child_catalog_item_id: id } });
  const namedByJobs = useRows('job_standard', { eq: { value: id } });
  // a job screen's unverified tag opens the editor here on the recipe the job item priced through, at the step or line
  // to sign (`?recipe=`, `?row=`; Spencer, 2026-10-16): that recipe is shown, its card is opened, the row is scrolled to and lit
  const [params] = useSearchParams();
  const wantRow = params.get('row');
  const [recipeId, setRecipeId] = useState<string | null>(params.get('recipe'));
  const scrolled = useRef(false);
  // read: the rail and the cards. inputs / calcs: that table wide, with what it needs beside it.
  const [mode, setMode] = useState<'read' | 'inputs' | 'calcs'>('read');
  const [railFolded, setRailFolded] = useState(false);
  const { lib } = useLibrary();
  // verifying from here (Spencer, 2026-10-12): an operation's minutes and a material's price are read and signed on the
  // step or line that uses them, never on another tab; what changed since a signed row came back is said under it
  const histories = useVerificationHistories();
  const look = useLook();
  const minutesChange = useStandardsChange();
  const { user } = useSession();
  const [signing, setSigning] = useState(false);
  useCrumb([{ label: 'Library', to: '/library' }, { label: 'Catalog items', to: '/library/catalog' }, { label: item?.name ?? '…' }]);

  const canEdit = useSession().canEdit('screen.recipe_editor');
  const dragInputs = useDragRows('catalog_input', inputs.rows, !canEdit);
  const dragCalcs = useDragRows('catalog_derived', derived.rows, !canEdit);
  const dragChildren = useDragRows('catalog_child', children.rows, !canEdit);
  const recipe = recipes.rows.find((r) => r.id === recipeId) ?? recipes.rows.find((r) => r.is_default) ?? recipes.rows[0];
  // what changed on the recipe since it was verified: its steps and lines are lit where they are read (Spencer, 2026-10-17)
  const recipeSince = recipe && !recipe.verified ? histories.get(`recipe:${recipe.id}`) : undefined;
  const recipeParts = recipe ? parts.rows.filter((p) => p.recipe_id === recipe.id) : [];
  const recipeSteps = recipe ? steps.rows.filter((s) => s.recipe_id === recipe.id) : [];
  const recipeBom = recipe ? bom.rows.filter((b) => b.recipe_id === recipe.id) : [];
  const dragSteps = useDragRows('recipe_route_step', recipeSteps, !canEdit);
  const dragBom = useDragRows('recipe_bom_line', recipeBom, !canEdit);
  const dragParts = useDragRows('catalog_part', recipeParts, !canEdit);
  const rowInRoute = !!wantRow && recipeSteps.some((s) => s.id === wantRow);
  const rowInBom = !!wantRow && recipeBom.some((b) => b.id === wantRow);
  useEffect(() => {
    if (scrolled.current || !wantRow || !(rowInRoute || rowInBom)) return;
    if (!document.getElementById(`row-${wantRow}`)) return;
    scrolled.current = true;
    // rows above keep arriving for a moment after the first paint (the rail, the lists the pickers read) and push the row
    // away, so it is kept centred until they settle, unless the person has started moving on their own
    let moved = false;
    const quit = () => {
      moved = true;
    };
    for (const ev of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.addEventListener(ev, quit, { once: true, passive: true });
    const go = () => {
      if (!moved) document.getElementById(`row-${wantRow}`)?.scrollIntoView({ block: 'center' });
    };
    go();
    for (const ms of [250, 700, 1500, 3000]) window.setTimeout(go, ms);
  }, [wantRow, rowInRoute, rowInBom]);

  if (!item) return <Loading />;
  const save = (table: string, rowId: string, patch: Row) => updateRow(table, rowId, patch);
  /** A retired row's mark and its Restore (2026-10-23): the row comes back on the item; restoring is an edit, so the recipe stays unverified until signed. */
  const Retired = ({ row, table }: { row: Row; table: string }) =>
    row.retired_at ? (
      <span className="nowrap retired-mark">
        <Tag kind="off" title={`Retired ${String(row.retired_at).slice(0, 10)}: out of the item the engine prices, kept here`}>retired</Tag>{' '}
        {canEdit && (
          <Btn small onClick={() => void save(table, row.id, { retired_at: null })} title="Back on the item">
            Restore
          </Btn>
        )}
      </span>
    ) : null;
  const stdByKey = new Map((lib?.standards ?? []).map((d) => [d.key, d]));
  const followed = (i: Row): LibStandardDef | undefined => (i.data_type === 'standard' ? stdByKey.get(String(i.standard_key || i.key)) : undefined);
  // what a line or part can be filled by: a material standard, a role a choice standard fills, or a material picked on this item
  const roles = [
    ...new Set([
      ...standards.rows.filter((s) => s.data_type === 'material').map((s) => s.key as string),
      ...standards.rows.flatMap((s) => (Array.isArray(s.choices) ? s.choices.flatMap((c: Row) => Object.keys(c.materials ?? {})) : [])),
      ...standards.rows.flatMap((s) => (s.roles && typeof s.roles === 'object' ? Object.keys(s.roles as Row) : [])),
      ...inputs.rows.filter((i) => i.data_type === 'material').map((i) => i.key as string),
    ]),
  ].sort();
  // what fills a role on this item: a followed-standard input (the box's own pick wins), else the job's standard
  /** The material a role resolves to under the company defaults today (the job may pick another): to read its price and verify it from here. */
  const roleDefault = (role: string): Row | undefined => {
    for (const def of standards.rows) {
      if (def.data_type === 'material' && def.key === role && def.default_value) return mats.rows.find((m) => m.id === def.default_value);
      const choice = ((def.choices ?? []) as { value: string; materials?: Record<string, string> }[]).find((c) => c.value === def.default_value);
      const id = choice?.materials?.[role];
      if (id) return mats.rows.find((m) => m.id === id);
    }
    return undefined;
  };
  const roleSource = (role: string): string => {
    for (const i of inputs.rows) {
      if (i.data_type !== 'standard') continue;
      const def = stdByKey.get(String(i.standard_key || i.key));
      if (!def) continue;
      const fills = new Set([...Object.keys(def.roles ?? {}), ...(def.choices ?? []).flatMap((c) => Object.keys(c.materials ?? {}))]);
      if (def.key === role || fills.has(role)) return `${role.replace(/_/g, ' ')} · from the ${i.label} input`;
    }
    return `the job's ${role.replace(/_/g, ' ')}`;
  };
  // the operation picker: grouped by phase in the Phases list's order, the operations in their list order (Spencer, 2026-10-08: note 73)
  const opsByPhase = (() => {
    const live = ops.rows.filter((o) => !o.retired);
    const phases = Object.values(lib?.phases ?? {}).sort((a, b) => a.sort - b.sort);
    const grouped = phases.map((p) => ({ key: p.id, label: p.name, ops: live.filter((o) => o.phase_id === p.id) }));
    const none = live.filter((o) => !o.phase_id || !lib?.phases[o.phase_id]);
    return [...grouped, { key: 'none', label: 'No phase', ops: none }].filter((g) => g.ops.length > 0);
  })();
  const verifiedRecipes = recipes.rows.filter((r) => r.verified).length;
  const recipeStandards = standards.rows.filter((s) => s.data_type === 'recipe' || s.data_type === 'choice');
  // what the recipe on screen rests on: the operations its steps name and the materials its lines and parts name
  const usedOps = [...new Set(recipeSteps.map((s) => s.operation_id as string))].map((oid) => ops.rows.find((o) => o.id === oid)).filter((o): o is Row => !!o);
  const usedMats = [...new Set([...recipeBom, ...recipeParts].map((b) => b.material_id as string | null).filter((m): m is string => !!m))].map((mid) => mats.rows.find((m) => m.id === mid)).filter((m): m is Row => !!m);
  const opsToSign = usedOps.filter((o) => !o.verified && lib?.operations[o.id]?.minutes_per_unit);
  const matsToSign = usedMats.filter((m) => !m.verified && m.price !== null && m.price !== '');
  const opsBlocked = usedOps.filter((o) => !o.verified && !lib?.operations[o.id]?.minutes_per_unit).length;
  const matsBlocked = usedMats.filter((m) => !m.verified && (m.price === null || m.price === '')).length;
  /** After reading every step and line: every operation and material this recipe uses that can be signed, in one tap. */
  const signUsed = async () => {
    setSigning(true);
    try {
      await updateRows('operation', opsToSign.map((o) => o.id), { verified: true, verified_by: user?.id ?? null });
      await updateRows('material', matsToSign.map((m) => m.id), { verified: true, verified_by: user?.id ?? null });
      refresh('verification_changes');
    } finally {
      setSigning(false);
    }
  };
  const minutesParse = (x: string) => (x.trim() === '' ? '' : /^\d*\.?\d+$/.test(x.trim()) ? x.trim() : undefined);
  const priceParse = (x: string) => (x.trim() === '' ? '' : /^\d*\.?\d+$/.test(x.trim().replace(/^\$/, '')) ? x.trim().replace(/^\$/, '') : undefined);
  const libItem = lib?.catalog[id];
  // the standards this item's formulas read, the tallies its parts name, and the roles whose sheet is read
  const read = libItem ? namesRead(libItem) : new Set<string>();
  const stdKeysRead = (lib?.standards ?? []).filter((d) => read.has(d.key)).map((d) => d.key);
  const tallies = [...new Set(parts.rows.map((p) => (p.material_role as string) || 'parts'))]; // a part's totals go by what it is cut from (Spencer, 2026-10-08)
  const rolesRead = [...new Set([...parts.rows.map((p) => p.material_role as string | null), ...bom.rows.map((b) => b.material_role as string | null)].filter((r): r is string => !!r))];

  /**
   * Deleting an item takes everything under it. It is refused while a job item is made from it, another item
   * creates it, or a standard names it: those are told, not broken.
   */
  const remove = async () => {
    const blockers: string[] = [];
    if (usedBy.rows.length) blockers.push(`${usedBy.rows.length} job item${usedBy.rows.length === 1 ? ' is' : 's are'} made from it (remove them from their jobs first)`);
    const parents = createdBy.rows.map((k) => cats.rows.find((c) => c.id === k.catalog_item_id)?.name ?? 'another item');
    if (parents.length) blockers.push(`${[...new Set(parents)].join(', ')} create${parents.length === 1 ? 's' : ''} it as a child (point that link elsewhere first)`);
    const defaults = standards.rows.filter((s) => s.default_value === id).map((s) => s.label as string);
    if (defaults.length) blockers.push(`the standard${defaults.length === 1 ? '' : 's'} ${defaults.join(', ')} default${defaults.length === 1 ? 's' : ''} to it (change the default first)`);
    if (namedByJobs.rows.length) blockers.push(`${namedByJobs.rows.length} job${namedByJobs.rows.length === 1 ? ' names' : 's name'} it in a job standard (change that on the job first)`);
    if (blockers.length) {
      window.alert(`"${item.name}" cannot be deleted yet: ${blockers.join('; ')}.`);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the catalog item "${item.name}"?\n\nIts inputs, calculations, children, recipes, route steps, BOM lines and parts go with it. This cannot be undone.`)) return;
    await deleteRow('catalog_item', id);
    nav('/library/catalog');
  };

  /** A copy brings inputs, calculations, children, recipes with their steps, lines and parts; it lands unverified and is independent from then on. */
  const copy = async () => {
    const name = window.prompt('Name for the copy:', `${item.name} (copy)`);
    if (!name || !name.trim()) return;
    const newId = await rpc<string>('copy_catalog_item', { p_id: id, p_name: name.trim() });
    refresh('catalog_item', 'catalog_input', 'catalog_derived', 'catalog_child', 'recipe', 'recipe_route_step', 'recipe_bom_line', 'catalog_part', 'checklist_line', 'job_type_catalog_item');
    nav(`/library/catalog/${newId}`);
  };

  // ---------- the reference rail ----------

  const railInputs = (
    <div className="panel rail-panel">
      <div className="lb">
        <span>Inputs ({inputs.rows.length})</span>
        <span className="nowrap">
          {mode === 'read' && canEdit && (
            <Btn small onClick={() => setMode('inputs')}>
              Edit inputs
            </Btn>
          )}
          {mode === 'read' && (
            <>
              {' '}
              <Btn small read title={railFolded ? 'Show values' : 'Keys only'} onClick={() => setRailFolded(!railFolded)}>
                {railFolded ? '›' : '‹'}
              </Btn>
            </>
          )}
        </span>
      </div>
      {inputs.rows.length === 0 && <small>No inputs yet.</small>}
      {inputs.rows.map((i) => (
        <RailRow key={i.id} k={i.key} v={railFolded ? undefined : inputWords(i, followed(i))} title={i.label} />
      ))}
    </div>
  );
  const railCalcs = (
    <div className="panel rail-panel">
      <div className="lb">
        <span>Calculations ({derived.rows.length})</span>
        {mode === 'read' && canEdit && (
          <Btn small onClick={() => setMode('calcs')}>
            Edit calculations
          </Btn>
        )}
      </div>
      {derived.rows.length === 0 && <small>None yet.</small>}
      {derived.rows.map((d) => (
        <RailRow key={d.id} k={d.key} title={`${d.label} = ${d.expr}`} />
      ))}
    </div>
  );
  const railAlso = !railFolded && (
    <div className="panel rail-panel">
      <div className="lb">
        <span>Also readable</span>
        <Info left>
          Names every formula on this item can use beside its inputs and calculations. Every formula is worked out per box; the job's total of each step and line is rounded up to whole units once, and each box carries its share. Standards are the job's values. area_, count_ and band_ by what a part is cut from come from the cut list; thickness_ and sheet_area_ from the sheet filling a role. Functions: if, roundup, rounddown, round, ceiling, floor, min, max, abs, blank. A vendor order item reads the whole job with pool_sum, pool_count and pool_count_if.
        </Info>
      </div>
      {stdKeysRead.length > 0 && <RailRow k={stdKeysRead.join(', ')} v="standards" />}
      {tallies.map((t) => (
        <RailRow key={t} k={`area_${t}, count_${t}, band_${t}`} v="cut list" />
      ))}
      {rolesRead.map((r) => (
        <RailRow key={r} k={`thickness_${r}, sheet_area_${r}`} v="the sheet" />
      ))}
      <RailRow k="recipe_name" v={recipes.rows.map((r) => r.name).join(' | ') || 'the recipe'} />
      {children.rows.length > 0 && <RailRow k="child_index" v="in a child's formulas" />}
      <RailRow k="pool_sum(), pool_count(), pool_count_if()" v="the whole job, on a vendor order item" />
    </div>
  );

  // ---------- the inputs table (edit mode) ----------

  const inputsTable = (
    <div className="panel">
      <div className="lb">
        <span>Inputs ({inputs.rows.length}) · these become the grid columns</span>
        <span className="nowrap">
          {canEdit && (
            <Btn small onClick={() => void insertRow('catalog_input', { catalog_item_id: id, key: `input_${inputs.rows.length + 1}`, label: 'New input', sort: inputs.rows.length + 1 })}>
              + Input
            </Btn>
          )}{' '}
          {canEdit && standards.rows.some((s) => s.kind === 'catalog') && (
            <select
              style={{ width: 'auto', maxWidth: 190, verticalAlign: 'middle' }}
              value=""
              title="A pre-staged input (Library → Standards, kind catalog): pulled in with its type, choices, default and note, typed per item on the job"
              onChange={(e) => {
                const s = standards.rows.find((x) => x.key === e.target.value);
                if (!s) return;
                if (inputs.rows.some((i) => i.key === s.key)) {
                  toast(`This item already has an input keyed ${s.key}.`);
                  return;
                }
                void insertRow('catalog_input', { catalog_item_id: id, key: s.key, label: s.label, data_type: 'standard', standard_key: s.key, info: s.info ?? null, sort: inputs.rows.length + 1 });
              }}
            >
              <option value="">+ Standard input…</option>
              {standards.rows
                .filter((s) => s.kind === 'catalog')
                .map((s) => (
                  <option key={s.key} value={s.key} disabled={inputs.rows.some((i) => i.key === s.key)}>
                    {s.label}
                  </option>
                ))}
            </select>
          )}{' '}
          <Btn small primary onClick={() => setMode('read')}>
            Done
          </Btn>
        </span>
      </div>
      {inputs.rows.length === 0 && <Empty>No inputs yet: the fields a job will fill.</Empty>}
      <div className="tblwrap">
        <table className="fixed" style={{ minWidth: 1200 }}>
          <colgroup>
            <col style={{ width: 28 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 170 }} />
            {/* wide enough for "follows a standard", its longest option, so the type is never cut short (design review D4) */}
            <col style={{ width: 180 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 60 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 150 }} />
            <col />
            <col style={{ width: 36 }} />
          </colgroup>
          {inputs.rows.length > 0 && (
            <thead>
              <tr>
                <th></th>
                <th>Label</th>
                <th>Key</th>
                <th>Type</th>
                <th>Default</th>
                <th>Unit</th>
                <th>
                  Warns when
                  <Info>A formula over the item's inputs. When true, the item shows the warning; it never blocks.</Info>
                </th>
                <th>
                  Hidden when
                  <Info>A formula over the item's other inputs. When true, this input is not shown on the item (it keeps its value and default): movable_shelves = 0 hides the shelf-hole choice.</Info>
                </th>
                <th>
                  Note behind the i dot
                  <Info>What the label does not say: whether the height is with or without the toe kick. It shows behind an i dot wherever this input is typed.</Info>
                </th>
                <th></th>
              </tr>
            </thead>
          )}
          <tbody>
            {inputs.rows.map((i) => {
              const def = followed(i);
              const keyIsStandard = i.data_type !== 'standard' && standards.rows.some((s) => s.key === i.key);
              const detail = i.data_type === 'choice' || i.data_type === 'material' || i.data_type === 'standard' || keyIsStandard;
              return [
                <tr key={i.id} {...dragInputs.row(i.id)}>
                  <td>
                    <Grip {...dragInputs.grip(i.id)} />
                    <Retired row={i} table="catalog_input" />
                  </td>
                  <td>
                    <Edit className="cell" disabled={!canEdit} value={i.label} onSave={(v) => save('catalog_input', i.id, { label: v || i.label })} />
                  </td>
                  <td>
                    <Edit className="cell mono" disabled={!canEdit} value={i.key} parse={snake} onSave={(v) => save('catalog_input', i.id, { key: v })} />
                  </td>
                  <td>
                    <select className="cell" disabled={!canEdit} value={i.data_type} onChange={(e) => void save('catalog_input', i.id, { data_type: e.target.value, ...(e.target.value !== 'choice' ? { choices: null } : {}) })}>
                      {[
                        ['number', 'number'],
                        ['text', 'text'],
                        ['boolean', 'yes / no'],
                        ['choice', 'choice'],
                        ['material', 'material'],
                        ['standard', 'follows a standard'],
                      ].map(([t, l]) => (
                        <option key={t} value={t}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {i.data_type === 'standard' ? (
                      <small className="muted">the standard's</small>
                    ) : i.data_type === 'material' ? (
                      <small className="muted" title={lib?.materials[i.default_value ?? '']?.description}>
                        {lib?.materials[i.default_value ?? '']?.description ?? '—'}
                      </small>
                    ) : i.data_type === 'choice' ? (
                      <select className="cell" disabled={!canEdit} value={i.default_value ?? ''} onChange={(e) => void save('catalog_input', i.id, { default_value: e.target.value || null })}>
                        <option value="">—</option>
                        {(Array.isArray(i.choices) ? i.choices : []).map((c: string) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    ) : i.data_type === 'boolean' ? (
                      <select className="cell" disabled={!canEdit} value={i.default_value ?? ''} onChange={(e) => void save('catalog_input', i.id, { default_value: e.target.value || null })}>
                        <option value="">—</option>
                        <option value="true">yes</option>
                        <option value="false">no</option>
                      </select>
                    ) : (
                      <Edit className="cell" disabled={!canEdit} value={i.default_value ?? ''} onSave={(v) => save('catalog_input', i.id, { default_value: v || null })} />
                    )}
                  </td>
                  <td>{i.data_type === 'number' || i.data_type === 'text' ? <Edit className="cell" disabled={!canEdit} value={i.unit ?? ''} onSave={(v) => save('catalog_input', i.id, { unit: v || null })} /> : <small className="muted">{def?.unit ?? ''}</small>}</td>
                  <td>
                    <Edit className="cell mono" disabled={!canEdit} value={i.rule_expr ?? ''} parse={formulaOrBlank} onSave={(v) => save('catalog_input', i.id, { rule_expr: v || null })} />
                    {i.rule_expr && <Edit className="cell" disabled={!canEdit} value={i.rule_message ?? ''} placeholder="what the warning says" onSave={(v) => save('catalog_input', i.id, { rule_message: v || null })} />}
                  </td>
                  <td>
                    <Edit className="cell mono" disabled={!canEdit} value={i.hidden_expr ?? ''} parse={formulaOrBlank} onSave={(v) => save('catalog_input', i.id, { hidden_expr: v || null })} />
                  </td>
                  <td>
                    <Edit className="cell" disabled={!canEdit} value={i.info ?? ''} onSave={(v) => save('catalog_input', i.id, { info: v.trim() || null })} />
                  </td>
                  <td>{canEdit && <Btn small title="Remove this input" onClick={() => window.confirm(`Remove the input "${i.label}"?`) && void deleteRow('catalog_input', i.id)}>×</Btn>}</td>
                </tr>,
                detail && (
                  <tr key={`${i.id}-detail`} className="detail">
                    <td></td>
                    <td colSpan={9}>
                      {keyIsStandard && <div className="warn">A standard has this key, and its value wins over what is typed here. Set the type to "follows a standard" so the item says so.</div>}
                      {i.data_type === 'choice' && (
                        <div className="row mid" style={{ gap: 10, alignItems: 'flex-start' }}>
                          <small className="muted">↳ choices</small>
                          <div className="grow" style={{ maxWidth: 520 }}>
                            <ListEdit value={Array.isArray(i.choices) ? i.choices.map(String) : []} disabled={!canEdit} hooks={{ one: 'choice' }} onSave={(v) => save('catalog_input', i.id, { choices: v.length ? v : null })} />
                          </div>
                        </div>
                      )}
                      {i.data_type === 'material' && lib && (
                        <div className="row mid" style={{ gap: 10 }}>
                          <small className="muted">↳ picker offers</small>
                          <FilterEditor lib={lib} value={i.material_filter ?? null} disabled={!canEdit} onChange={(f) => void save('catalog_input', i.id, { material_filter: f })} />
                          <small className="muted">default</small>
                          <MaterialPick lib={lib} value={i.default_value ?? ''} filter={i.material_filter ?? null} onPick={(mid) => void save('catalog_input', i.id, { default_value: mid })} />
                        </div>
                      )}
                      {i.data_type === 'standard' && (
                        <div className="row mid" style={{ gap: 10, flexWrap: 'wrap' }}>
                          <small className="muted">↳ follows</small>
                          <select disabled={!canEdit} value={i.standard_key ?? ''} onChange={(e) => void save('catalog_input', i.id, { standard_key: e.target.value || null })} title="The standard this input follows: its value, type and choices come from there">
                            <option value="">({i.key}){standards.rows.some((s) => s.key === i.key) ? '' : ' · no standard has this key'}</option>
                            <optgroup label="Catalog inputs (pre-staged, typed per item)">
                              {standards.rows
                                .filter((s) => s.kind === 'catalog')
                                .map((s) => (
                                  <option key={s.key} value={s.key}>
                                    {s.label} ({s.key})
                                  </option>
                                ))}
                            </optgroup>
                            <optgroup label="Company and job standards (the job's value)">
                              {standards.rows
                                .filter((s) => s.kind !== 'catalog')
                                .map((s) => (
                                  <option key={s.key} value={s.key}>
                                    {s.label} ({s.key})
                                  </option>
                                ))}
                            </optgroup>
                          </select>
                          {def && (
                            <small>
                              <code>{def.key}</code>
                              {def.choices && def.choices.length > 0 && <> · choices {def.choices.map((c) => c.value).join(' | ')}</>}
                              {def.roles && Object.keys(def.roles).length > 0 && <> · fills {Object.keys(def.roles).join(', ')}</>}
                              {def.data_type !== 'choice' && <> · {def.data_type}</>}
                              {def.kind === 'catalog'
                                ? ` · a catalog input: typed per item on the job, starts at ${def.default_value === null || def.default_value === '' ? 'blank' : String(def.default_value)}`
                                : def.allow_item_override === false
                                  ? ' · no item may hold its own value'
                                  : ' · an item may hold its own value'}
                            </small>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ---------- the calculations table (edit mode) ----------

  const calcsTable = (
    <div className="panel">
      <div className="lb">
        <span>
          Calculations ({derived.rows.length})
          <Info>Values the formulas can use, each worked out per box from its inputs, the job's standards and the cut list. A vendor order item may read the whole job with pool_sum, pool_count and pool_count_if.</Info>
        </span>
        <span className="nowrap">
          {canEdit && (
            <Btn small onClick={() => void insertRow('catalog_derived', { catalog_item_id: id, key: `value_${derived.rows.length + 1}`, label: 'New calculation', expr: '0', sort: derived.rows.length + 1 })}>
              + Calculation
            </Btn>
          )}{' '}
          <Btn small primary onClick={() => setMode('read')}>
            Done
          </Btn>
        </span>
      </div>
      {derived.rows.length === 0 && <Empty>None yet.</Empty>}
      <div className="tblwrap">
        <table className="fixed" style={{ minWidth: 820 }}>
          <colgroup>
            <col style={{ width: 28 }} />
            <col style={{ width: 280 }} />
            <col style={{ width: 170 }} />
            <col />
            <col style={{ width: 36 }} />
          </colgroup>
          {derived.rows.length > 0 && (
            <thead>
              <tr>
                <th></th>
                <th>Label</th>
                <th>Key</th>
                <th>Formula</th>
                <th></th>
              </tr>
            </thead>
          )}
          <tbody>
            {derived.rows.map((d) => (
              <tr key={d.id} {...dragCalcs.row(d.id)}>
                <td>
                  <Grip {...dragCalcs.grip(d.id)} />
                  <Retired row={d} table="catalog_derived" />
                </td>
                <td>
                  <Edit className="cell" disabled={!canEdit} value={d.label} onSave={(v) => save('catalog_derived', d.id, { label: v || d.label })} />
                </td>
                <td>
                  <Edit className="cell mono" disabled={!canEdit} value={d.key} parse={snake} onSave={(v) => save('catalog_derived', d.id, { key: v })} />
                </td>
                <td>
                  <Edit className="cell mono" disabled={!canEdit} value={d.expr} parse={formula} onSave={(v) => save('catalog_derived', d.id, { expr: v })} />
                </td>
                <td>{canEdit && <Btn small title="Remove this calculation" onClick={() => window.confirm(`Are you sure you want to remove the calculation "${d.label}"? Formulas that read it will stop working.`) && void deleteRow('catalog_derived', d.id)}>×</Btn>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  /** In Edit inputs: every formula that reads this item, so what an input feeds is beside the table. */
  const railReadBy = (
    <div className="panel rail-panel">
      <div className="lb">
        <span>Read by{recipe ? ` · ${recipe.name}` : ''}</span>
      </div>
      {derived.rows.map((d) => (
        <RailRow key={`d${d.id}`} k={d.expr} v={`→ ${d.key}`} />
      ))}
      {recipeSteps.map((s) => (
        <RailRow key={`s${s.id}`} k={s.qty_expr} v={`route · ${lib?.operations[s.operation_id]?.name ?? ''}`} />
      ))}
      {recipeBom.map((b) => (
        <RailRow key={`b${b.id}`} k={b.qty_expr} v={`BOM · ${b.material_role ? b.material_role : lib?.materials[b.material_id ?? '']?.description ?? b.label ?? ''}`} />
      ))}
      {recipeParts.map((p) => (
        <RailRow key={`p${p.id}`} k={`${p.qty_expr} · ${p.length_expr} × ${p.width_expr}`} v={`cut list · ${p.name}`} />
      ))}
      {children.rows.flatMap((k) => Object.entries((k.input_map ?? {}) as Record<string, string>).map(([key, expr]) => <RailRow key={`${k.id}${key}`} k={expr} v={`${k.label || 'child'} · ${key}`} />))}
      {derived.rows.length + recipeSteps.length + recipeBom.length + recipeParts.length + children.rows.length === 0 && <small>Nothing reads the inputs yet.</small>}
    </div>
  );

  // ---------- the cards ----------

  const recipesPanel = (
    <Panel
      label="Recipes · one per way of getting this item"
      right={
        <>
          <Info left>
            Shop-built, Cabinotch, a second vendor: name each recipe as you like and add as many as the item has ways of being got. A job standard such as Box build picks a recipe by name on every item at once, so a recipe that should flip with it carries the same name as on the other items (the names in use are offered as you type); any other name is this item's own, picked on the job item itself or by an add-on. A new recipe can start as a copy of one the item already has.
          </Info>{' '}
          {canEdit && (
            <Btn small onClick={() => setAddingRecipe(true)}>
              + Recipe
            </Btn>
          )}
          {addingRecipe && <NewRecipe itemId={id} recipes={recipes.rows} onClose={() => setAddingRecipe(false)} onMade={setRecipeId} />}
          <datalist id="recipe-names">
            {namesInUse.map((n) => (
              <option key={n.name} value={n.name} />
            ))}
          </datalist>
        </>
      }
    >
      {recipes.rows.length === 0 ? (
        <Empty>No recipe yet, so this item prices at zero.</Empty>
      ) : (
        <>
          <Seg items={recipes.rows.map((r) => ({ key: r.id as string, label: `${r.name}${r.is_default ? ' (default)' : ''}` }))} value={recipe?.id ?? ''} onChange={setRecipeId} />
          {recipe && (
            <div className="row mid" style={{ marginTop: 8, gap: 10 }}>
              <Edit
                className="short"
                disabled={!canEdit}
                value={recipe.name}
                list="recipe-names"
                parse={(t) => (t.trim() && !recipes.rows.some((r) => r.id !== recipe.id && r.name.toLowerCase() === t.trim().toLowerCase()) ? t.trim() : undefined)}
                onSave={(v) => save('recipe', recipe.id, { name: v })}
                placeholder="Recipe name"
              />
              <small>
                v{recipe.version}
                {(() => {
                  const others = (namesInUse.find((n) => n.name.toLowerCase() === String(recipe.name).trim().toLowerCase())?.items ?? 1) - 1;
                  return others ? ` · ${others} other item${others === 1 ? ' has' : 's have'} a recipe of this name` : ' · only this item uses this name';
                })()}
              </small>
              {!recipe.is_default && canEdit && (
                <Btn small onClick={async () => { for (const r of recipes.rows) if (r.is_default) await save('recipe', r.id, { is_default: false }); await save('recipe', recipe.id, { is_default: true }); }}>
                  Make default
                </Btn>
              )}
              <span className="nowrap" title="A formula over the item's inputs. When true, this recipe is not available for that item: it is grayed in the item's recipe picker, and an item still resolving through it is flagged and priced through it anyway.">
                <small>unavailable when</small>{' '}
                <Edit className="mono" disabled={!canEdit} value={recipe.unavailable_expr ?? ''} parse={formulaOrBlank} onSave={(v) => save('recipe', recipe.id, { unavailable_expr: v || null })} style={{ width: 200 }} />
              </span>
              <span className="grow" />
              <VerifyButton table="recipe" row={recipe} />
              {canEdit && !recipe.is_default && (
                <Btn small onClick={async () => { if (!window.confirm(`Delete the recipe "${recipe.name}"?`)) return; setRecipeId(null); await deleteRow('recipe', recipe.id); }}>
                  Delete this recipe
                </Btn>
              )}
            </div>
          )}
          {recipe && (
            <div className="row mid rests" style={{ marginTop: 8, gap: 10 }}>
              <small>
                Rests on {usedOps.length} {usedOps.length === 1 ? 'operation' : 'operations'} ({usedOps.filter((o) => o.verified).length} verified) and {usedMats.length} {usedMats.length === 1 ? 'material' : 'materials'} ({usedMats.filter((m) => m.verified).length} verified).
                {opsBlocked > 0 && <> {opsBlocked} {opsBlocked === 1 ? 'operation has' : 'operations have'} no minutes yet.</>}
                {matsBlocked > 0 && <> {matsBlocked} {matsBlocked === 1 ? 'material has' : 'materials have'} no price yet.</>}
              </small>
              <span className="grow" />
              {can('verify') && opsToSign.length + matsToSign.length > 0 && (
                <Btn small primary disabled={signing} onClick={() => void signUsed()} title="After reading every step and line below: signs each operation and material this recipe uses, in one tap. The recipe itself is signed with its own Verify.">
                  Verify the {opsToSign.length > 0 ? `${opsToSign.length} ${opsToSign.length === 1 ? 'operation' : 'operations'}` : ''}{opsToSign.length > 0 && matsToSign.length > 0 ? ' and ' : ''}{matsToSign.length > 0 ? `${matsToSign.length} ${matsToSign.length === 1 ? 'material' : 'materials'}` : ''} it uses
                </Btn>
              )}
            </div>
          )}
          {recipe && !recipe.verified && <Since h={recipeSince} look={look} lit={(c) => (c.table === 'recipe_route_step' || c.table === 'recipe_bom_line') && c.kind !== 'removed'} />}
        </>
      )}
    </Panel>
  );

  const routeCard = recipe && (
    <Card
      itemId={id}
      k="route"
      force={rowInRoute}
      label={`Route · ${recipeSteps.filter((s) => !s.retired_at).length} steps · ${recipe.name}${retiredWords(retiredIn(stepsAll, (x) => x.recipe_id === recipe.id))}`}
      right={
        <>
          <Info left>Each step is an operation and how many of it per box, as a plain sentence with the formula under it. The names a formula can use are in the rail on the left. The job's total of a step is rounded up to whole units once (3.5 sheets → 4), and each box carries its share. Setup is paid once per job (or per room, when pooling is by room), or once per batch of the job's total when the operation has a batch size; untick setup on a step that should carry none.</Info>{' '}
          {canEdit && ops.rows[0] && (
            <Btn
              small
              onClick={async () => {
                // a new step lands at the bottom as the first operation on the list, so say so: a second click looked like nothing (Spencer, 2026-10-14)
                await insertRow('recipe_route_step', { recipe_id: recipe.id, operation_id: ops.rows[0]!.id, qty_expr: '0', sort: recipeSteps.length + 1 });
                toast(`Step ${recipeSteps.length + 1} added at the bottom as ${ops.rows[0]!.name}: pick its operation and type how many.`);
              }}
            >
              + Step
            </Btn>
          )}
        </>
      }
    >
      {recipeSteps.map((s) => {
        const op = lib?.operations[s.operation_id];
        const opRow = ops.rows.find((o) => o.id === s.operation_id);
        const cls = op?.labor_class_id ? lib?.laborClasses[op.labor_class_id] : undefined;
        const unit = (op?.unit || 'unit').replace(/^\//, '');
        const phase = op?.phase_id ? lib?.phases[op.phase_id]?.name : undefined;
        const changed = changesOn(recipeSince, 'recipe_route_step', s.id);
        return (
          <div key={s.id} id={`row-${s.id}`} style={{ display: 'block' }} {...dragSteps.row(s.id, `ln dragrow${s.id === wantRow ? ' hit' : ''}${changed.length ? ' changed' : ''}${s.retired_at ? ' retired-row' : ''}`)}>
            <div className="row mid" style={{ gap: 6 }}>
              <Grip {...dragSteps.grip(s.id)} />
              <Retired row={s} table="recipe_route_step" />
              <select disabled={!canEdit} value={s.operation_id} onChange={(e) => void save('recipe_route_step', s.id, { operation_id: e.target.value })} style={{ width: 260 }}>
                {opsByPhase.map((g) => (
                  <optgroup key={g.key} label={g.label}>
                    {g.ops.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
                {op && !opsByPhase.some((g) => g.ops.some((o) => o.id === op.id)) && <option value={op.id}>{op.name} (retired)</option>}
              </select>
              {phase && <small className="muted nowrap">{phase}</small>}
              <span className="grow">
                <Edit className="cell" disabled={!canEdit} value={s.note ?? ''} placeholder="The plain sentence: one per box" onSave={(v) => save('recipe_route_step', s.id, { note: v || null })} />
              </span>
              {canEdit && <Btn small onClick={() => window.confirm(`Are you sure you want to remove the route step "${op?.name ?? 'this step'}" from the ${recipe.name} recipe?`) && void deleteRow('recipe_route_step', s.id)}>×</Btn>}
            </div>
            {changed.length > 0 && (
              <div style={{ paddingLeft: 22 }}>
                <ChangeNote changes={changed} look={look} />
              </div>
            )}
            <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
              <small>how many {plural(unit)} =</small>
              <span className="grow">
                <Edit className="cell mono" disabled={!canEdit} value={s.qty_expr} parse={formula} onSave={(v) => save('recipe_route_step', s.id, { qty_expr: v })} />
              </span>
              <label className="check">
                <input type="checkbox" disabled={!canEdit} checked={s.charge_setup} onChange={(e) => void save('recipe_route_step', s.id, { charge_setup: e.target.checked })} /> setup
              </label>
            </div>
            {op && opRow && (
              <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
                <small>takes</small>
                <Edit
                  className="cell short mono right"
                  disabled={!canEdit || minutesChange.busy}
                  value={op.minutes_per_unit ?? ''}
                  placeholder="minutes"
                  parse={minutesParse}
                  onSave={(v) => minutesChange.change('time_standard', { operation_id: op.id }, 'minutes_per_unit', v === '' ? null : v, `${op.name}: ${op.minutes_per_unit ?? 'none'} → ${v === '' ? 'none' : v} min ${op.unit ?? ''}`)}
                />
                <small className="grow">
                  min per {unit}
                  {op.setup_minutes ? ` + ${qty(op.setup_minutes)} min setup ${op.batch_size ? `per batch of ${qty(op.batch_size)}` : 'once per job'}` : ''}
                  {' · '}
                  {cls ? `${cls.name}${lib?.rates[cls.id] ? ` at $${Number(lib.rates[cls.id]).toFixed(2)}/hr` : ', no rate'}` : <span className="warn">no labor class</span>}
                  {!op.minutes_per_unit && <> · <span className="warn">no minutes yet: this step prices at zero</span></>}
                </small>
                <VerifyButton table="operation" row={opRow} />
              </div>
            )}
            {op && !opRow?.verified && histories.get(`operation:${op.id}`)?.changes.length ? (
              <div style={{ paddingLeft: 22 }}>
                <Since h={histories.get(`operation:${op.id}`)} look={look} compact />
              </div>
            ) : null}
          </div>
        );
      })}
      {recipeSteps.length === 0 && <Empty>No steps yet.</Empty>}
    </Card>
  );

  const bomCard = recipe && (
    <Card
      itemId={id}
      k="bom"
      force={rowInBom}
      label={`BOM · ${recipeBom.filter((b) => !b.retired_at).length} lines · ${recipe.name}${retiredWords(retiredIn(bomAll, (x) => x.recipe_id === recipe.id))}`}
      right={canEdit && mats.rows[0] ? <Btn small onClick={() => void insertRow('recipe_bom_line', { recipe_id: recipe.id, material_id: mats.rows[0]!.id, qty_expr: '0', sort: recipeBom.length + 1 })}>+ Line</Btn> : undefined}
    >
      {recipeBom.map((b) => (
        <div key={b.id} id={`row-${b.id}`} style={{ display: 'block' }} {...dragBom.row(b.id, `ln dragrow${b.id === wantRow ? ' hit' : ''}${changesOn(recipeSince, 'recipe_bom_line', b.id).length ? ' changed' : ''}${b.retired_at ? ' retired-row' : ''}`)}>
          <div className="row mid" style={{ gap: 6 }}>
            <Grip {...dragBom.grip(b.id)} />
            <Retired row={b} table="recipe_bom_line" />
            <select
              disabled={!canEdit}
              value={b.material_id ?? (b.material_role ? `role:${b.material_role}` : 'typed')}
              onChange={(e) =>
                void save(
                  'recipe_bom_line',
                  b.id,
                  e.target.value === 'typed'
                    ? { material_id: null, material_role: null, unit_price_expr: b.unit_price_expr || (standards.rows.find((s) => s.data_type === 'number' && s.kind === 'job')?.key ?? '0') }
                    : e.target.value.startsWith('role:')
                      ? { material_id: null, material_role: e.target.value.slice(5) }
                      : { material_id: e.target.value, material_role: null },
                )
              }
              style={{ width: 260 }}
            >
              <optgroup label="A price typed on the job">
                <option value="typed">a typed price, no material</option>
              </optgroup>
              <optgroup label="Filled by an input or a job standard">
                {[...new Set([...roles, ...(b.material_role ? [b.material_role as string] : [])])].map((r) => (
                  <option key={r} value={`role:${r}`}>
                    {roleSource(r)}
                  </option>
                ))}
              </optgroup>
              <optgroup label="A fixed material">
                {mats.rows.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.description}
                  </option>
                ))}
              </optgroup>
            </select>
            <span className="grow">
              <Edit className="cell" disabled={!canEdit} value={b.note ?? ''} placeholder="The plain sentence: 2 hinges per door, 3 over 40 inches" onSave={(v) => save('recipe_bom_line', b.id, { note: v || null })} />
            </span>
            {canEdit && (
              <Btn
                small
                onClick={() =>
                  window.confirm(`Are you sure you want to remove the BOM line "${mats.rows.find((m) => m.id === b.material_id)?.description ?? b.label ?? (b.material_role ? `the job's ${String(b.material_role).replace(/_/g, ' ')}` : 'this line')}" from the ${recipe.name} recipe?`) &&
                  void deleteRow('recipe_bom_line', b.id)
                }
              >
                ×
              </Btn>
            )}
          </div>
          {changesOn(recipeSince, 'recipe_bom_line', b.id).length > 0 && (
            <div style={{ paddingLeft: 22 }}>
              <ChangeNote changes={changesOn(recipeSince, 'recipe_bom_line', b.id)} look={look} />
            </div>
          )}
          <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
            <small>qty:</small>
            <span className="grow">
              <Edit className="cell mono" disabled={!canEdit} value={b.qty_expr} parse={formula} onSave={(v) => save('recipe_bom_line', b.id, { qty_expr: v })} />
            </span>
          </div>
          {!b.material_id && b.material_role && (() => {
            const m = roleDefault(b.material_role);
            if (!m) return null;
            return (
              <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
                <small className="grow">
                  today: {m.description} · {m.price === null || m.price === '' ? <span className="warn">no price yet</span> : priceWords(m)}
                  {' '}· the company default; a job may pick another
                </small>
                <VerifyButton table="material" row={m} />
              </div>
            );
          })()}
          {b.material_id && (() => {
            const m = mats.rows.find((x) => x.id === b.material_id);
            if (!m) return null;
            const h = m.verified ? undefined : histories.get(`material:${m.id}`);
            return (
              <>
                <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
                  <small>costs $</small>
                  <Edit
                    className="cell short mono right"
                    disabled={!canEdit}
                    value={m.price ?? ''}
                    placeholder="price"
                    parse={priceParse}
                    onSave={(v) => save('material', m.id, { price: v === '' ? null : v, price_updated: today() })}
                  />
                  <small className="grow">
                    per {priceWords(m).includes(' per pack of ') ? 'pack' : m.unit || 'each'}
                    {m.vendor_id ? ` · ${look('vendor', m.vendor_id)}` : ''}
                    {m.price_updated ? ` · price from ${String(m.price_updated).slice(0, 10)}` : ''}
                    {(m.price === null || m.price === '') && <> · <span className="warn">no price yet: this line prices at zero</span></>}
                  </small>
                  <VerifyButton table="material" row={m} />
                </div>
                <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
                  <PackFields m={m} disabled={!canEdit} onSave={(patch) => save('material', m.id, patch)} />
                </div>
                {h?.changes.length ? (
                  <div style={{ paddingLeft: 22 }}>
                    <Since h={h} look={look} compact />
                  </div>
                ) : null}
              </>
            );
          })()}
          {!b.material_id && !b.material_role && (
            <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
              <small>called</small>
              <span className="grow">
                <Edit className="cell" disabled={!canEdit} value={b.label ?? ''} placeholder="Cabinotch order, as quoted" onSave={(v) => save('recipe_bom_line', b.id, { label: v || null })} />
              </span>
            </div>
          )}
          <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
            <small>price each =</small>
            <span className="grow">
              <Edit
                className="cell mono"
                disabled={!canEdit}
                value={b.unit_price_expr ?? ''}
                placeholder={b.material_id || b.material_role ? "blank: the material's own price" : 'cabinotch_price'}
                parse={formula}
                onSave={(v) => save('recipe_bom_line', b.id, { unit_price_expr: b.material_id || b.material_role ? v || null : v })}
              />
            </span>
            <Info left>
              A formula giving dollars per unit: a number typed on the item or the job (the Cabinotch price, a butcher block's listed price). On a line that names a material it takes the place of the material's price: the material still says what is bought, and
              Purchasing reads this price as what the job expects to pay (an allowance). Blank on the job means the line prices at zero and says so.
            </Info>
          </div>
          <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
            <small>vendor page =</small>
            <span className="grow">
              <Edit className="cell mono" disabled={!canEdit} value={b.link_expr ?? ''} placeholder="vendor_link (blank: none)" onSave={(v) => save('recipe_bom_line', b.id, { link_expr: formula(v) ?? null })} />
            </span>
            <Info left>A formula giving where this line is bought for the item, usually a text input on the item holding the web address (the butcher block's vendor link). It shows on the Item tab, the Materials tab and Purchasing, and goes onto the order so the buyer opens the page from the buy list.</Info>
          </div>
          <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
            <label className="row mid nowrap" style={{ gap: 4 }}>
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={!!b.separate_pricing}
                onChange={(e) => void save('recipe_bom_line', b.id, e.target.checked ? { separate_pricing: true } : { separate_pricing: false, markup_pct: null, ignore_margin: false })}
              />
              <small>Separate pricing</small>
            </label>
            {b.separate_pricing && (
              <>
                <small>Markup %</small>
                <Edit
                  className="cell short mono right"
                  disabled={!canEdit}
                  value={b.markup_pct ?? ''}
                  placeholder="company"
                  parse={(x) => (x.trim() === '' ? '' : /^\d*\.?\d+$/.test(x.trim().replace(/%$/, '')) ? x.trim().replace(/%$/, '') : undefined)}
                  onSave={(v) => save('recipe_bom_line', b.id, { markup_pct: v === '' ? null : v })}
                />
                <label className="row mid nowrap" style={{ gap: 4 }}>
                  <input type="checkbox" disabled={!canEdit} checked={!!b.ignore_margin} onChange={(e) => void save('recipe_bom_line', b.id, { ignore_margin: e.target.checked })} />
                  <small>Ignore margin</small>
                </label>
              </>
            )}
            <Info left>
              Off (the default): this line takes the company materials markup (Settings → Pricing percentages) and the gross margin. On: it takes its own markup (blank reads the company's), and with Ignore margin it is priced at cost plus that markup outside the gross margin (a large vendor order, a butcher block blank). Every line pays the company sales tax on its cost either way (Spencer, 2026-10-22).
            </Info>
          </div>
          <div className="row mid" style={{ gap: 6, paddingLeft: 22 }}>
            <small>sub work when</small>
            <span className="grow">
              <Edit className="cell mono" disabled={!canEdit} value={b.sub_expr ?? ''} placeholder="1 (always), or a formula; blank: never" onSave={(v) => save('recipe_bom_line', b.id, { sub_expr: formula(v) ?? null })} />
            </span>
            <Info left>A formula that is true when this line is subcontracted work: an outside finisher's number, typed on the item or worked out from it (square feet times a rate). It then takes the sub markup (Settings → Pricing percentages) and no gross margin, and counts as a sub on the Price tab, not a material; the shop's own hours on the route keep the full margin (Spencer, 2026-10-15). Type 1 for always.</Info>
          </div>
        </div>
      ))}
      {recipeBom.length === 0 && <Empty>No materials yet.</Empty>}
    </Card>
  );

  const cutListCard = recipe && (
    <Card
      itemId={id}
      k="parts"
      label={`Cut list · ${recipeParts.filter((p) => !p.retired_at).length} parts · ${recipe.name}${retiredWords(retiredIn(partsAll, (x) => x.recipe_id === recipe.id))}`}
      right={
        <>
          <Info left>
            For a recipe that makes the item in the shop: the parts it is cut from. They belong to this recipe; a bought recipe (Cabinotch) lists none. How many, how long and how wide are formulas over the item's inputs, its calculations and the job's standards. Length runs with the grain. A count of zero leaves a part out. The job's cut list is these, worked out for every item each time the job is priced.
            <br />
            <br />
            <b>Label</b> is what the part is called on the job: the item's number, a dash, and what is typed here (Top on L-05 reads L-05-Top). Blank means the part's name.
            <br />
            <br />
            The item's other formulas read the parts' totals by what they are cut from: <code>area_carcass_3_4</code> (square inches), <code>count_carcass_3_4</code> and <code>band_carcass_3_4</code> (inches of banded edge) add up every part cut from carcass 3/4. <code>parts_area</code>, <code>parts_count</code> and <code>parts_band</code> are the totals across every part.
          </Info>{' '}
          {canEdit && (
            <Btn small onClick={() => void insertRow('catalog_part', { recipe_id: recipe.id, name: 'New part', sort: recipeParts.length + 1 })}>
              + Part
            </Btn>
          )}
        </>
      }
    >
      {recipeParts.length === 0 && <Empty>No parts listed on this recipe. A bought recipe needs none; a shop-built one gets its cut list from them.</Empty>}
      {recipeParts.length > 0 && (
        <div className="tblwrap">
          <table className="fixed" style={{ minWidth: 1660 }}>
            <colgroup>
              <col style={{ width: 28 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 250 }} />
              <col style={{ width: 250 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 266 }} />
              <col style={{ width: 36 }} />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>Part</th>
                <th>Label</th>
                <th>How many</th>
                <th>Length (with the grain)</th>
                <th>Width</th>
                <th>
                  Material
                  <Info>What the part is cut from. "From the Carcass material input": the sheet the box's own Carcass material names (the job's choice unless this box holds its own), so a box with a different carcass is cut from its own sheet. "The job's …": a job standard no input on this item follows. "Always": one fixed sheet.</Info>
                </th>
                <th>
                  Grain
                  <Info>Which way the grain runs, and whether it may be turned when the part is nested on the sheet ("lock").</Info>
                </th>
                <th>
                  Banded edges: length · width
                  <Info>How many of the part's edges are banded, named by the part's own sides: edges along its length (0, 1 or 2), then edges along its width. The length is the part's size with the grain, which need not be the box's height.</Info>
                </th>
                <th>
                  Banding roll
                  <Info>
                    Which roll goes on the banded edges: a role the job fills (exterior_banding), or a material by its exact name, and it may depend on the inputs, so a shelf can band differently from the sides: if(shelf_edgeband = 'UVP Maple', "7/8 PreFin Maple Banding - Iron on 250'", 'exterior_banding'). The cut list names it on each piece.
                  </Info>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recipeParts.map((p) => [
                <tr key={p.id} {...dragParts.row(p.id)}>
                  <td>
                    <Grip {...dragParts.grip(p.id)} />
                    <Retired row={p} table="catalog_part" />
                  </td>
                  <td>
                    <Edit className="cell" disabled={!canEdit} value={p.name} onSave={(v) => save('catalog_part', p.id, { name: v || p.name })} />
                  </td>
                  <td>
                    <Edit className="cell" disabled={!canEdit} value={p.label ?? ''} placeholder={p.name} onSave={(v) => save('catalog_part', p.id, { label: v.trim() || null })} />
                  </td>
                  <td>
                    <Edit className="cell mono" disabled={!canEdit} value={p.qty_expr} parse={formula} onSave={(v) => save('catalog_part', p.id, { qty_expr: v })} />
                  </td>
                  <td>
                    <Edit className="cell mono" disabled={!canEdit} value={p.length_expr} parse={formula} onSave={(v) => save('catalog_part', p.id, { length_expr: v })} />
                  </td>
                  <td>
                    <Edit className="cell mono" disabled={!canEdit} value={p.width_expr} parse={formula} onSave={(v) => save('catalog_part', p.id, { width_expr: v })} />
                  </td>
                  <td>
                    <select
                      className="cell"
                      disabled={!canEdit}
                      value={p.material_id ? `m:${p.material_id}` : p.material_role ? `r:${p.material_role}` : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        void save('catalog_part', p.id, v.startsWith('m:') ? { material_id: v.slice(2), material_role: null } : v.startsWith('r:') ? { material_id: null, material_role: v.slice(2) } : { material_id: null, material_role: null });
                      }}
                    >
                      <option value="">Not named</option>
                      {roles.map((r) => (
                        <option key={r} value={`r:${r}`}>
                          {roleSource(r)}
                        </option>
                      ))}
                      {mats.rows.filter((m) => m.category === 'Sheet Goods' || m.id === p.material_id).map((m) => (
                        <option key={m.id} value={`m:${m.id}`}>
                          Always: {m.description}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className="row mid" style={{ gap: 4, flexWrap: 'nowrap' }}>
                      <select className="cell" disabled={!canEdit} value={p.grain} onChange={(e) => void save('catalog_part', p.id, { grain: e.target.value })}>
                        <option value="length">with the length</option>
                        <option value="width">with the width</option>
                        <option value="none">none</option>
                      </select>
                      <label className="check nowrap" title="Lock the grain direction: the part may not be turned when it is nested on the sheet">
                        <input type="checkbox" disabled={!canEdit || p.grain === 'none'} checked={!!p.grain_locked} onChange={(e) => void save('catalog_part', p.id, { grain_locked: e.target.checked })} />
                        lock
                      </label>
                    </span>
                  </td>
                  <td>
                    <span className="row mid" style={{ gap: 4, flexWrap: 'nowrap' }}>
                      <Edit className="cell mono" disabled={!canEdit} value={p.band_long_expr ?? ''} placeholder="0" parse={formulaOrBlank} onSave={(v) => save('catalog_part', p.id, { band_long_expr: v || null })} />
                      ·
                      <Edit className="cell mono" disabled={!canEdit} value={p.band_short_expr ?? ''} placeholder="0" parse={formulaOrBlank} onSave={(v) => save('catalog_part', p.id, { band_short_expr: v || null })} />
                    </span>
                  </td>
                  <td>
                    <Edit className="cell mono" disabled={!canEdit} value={p.band_material_expr ?? ''} placeholder="exterior_banding" parse={formulaOrBlank} onSave={(v) => save('catalog_part', p.id, { band_material_expr: v || null })} />
                  </td>
                  <td>{canEdit && <Btn small title="Remove this part" onClick={() => window.confirm(`Remove the part "${p.name}"?`) && void deleteRow('catalog_part', p.id)}>×</Btn>}</td>
                </tr>,
                // the note for the shop gets the whole width under its part, so a sentence can be read
                <tr key={`${p.id}-note`} className="detail">
                  <td></td>
                  <td colSpan={9}>
                    <span className="row mid" style={{ gap: 8 }}>
                      <small className="muted">↳ note for the shop</small>
                      <span className="grow">
                        <Edit className="cell" disabled={!canEdit} value={p.note ?? ''} onSave={(v) => save('catalog_part', p.id, { note: v || null })} />
                      </span>
                    </span>
                  </td>
                </tr>,
              ])}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );

  const typeStandards = standards.rows.filter((s) => s.data_type === 'catalog_item');
  const childrenCard = (
    <Card
      itemId={id}
      k="children"
      label={`Children it creates (${children.rows.filter((k) => !k.retired_at).length})${retiredWords(retiredIn(childrenAll))}`}
      right={
        <>
          <Info left>
            What the item makes on its own: a box makes its doors, sized by the box plus the reveal standards. Each child is its own card with a table of the child's inputs and the formula that fills each, over this item's inputs, its calculations and the job's standards. An item can make several kinds of thing, and two of them can be the same kind of item (doors and drawer fronts are both the job's door type); name each so they can be told apart on a job.
            <br />
            <br />
            In a child's formula, <code>child_index</code> is that child's position among the ones the card creates, counting from 0. A drawer front can use it to pick its own height: <code>if(child_index = 0, drawer_1_height, drawer_2_height)</code>.
          </Info>{' '}
          {canEdit && (
            <Btn small onClick={() => void insertRow('catalog_child', { catalog_item_id: id, child_catalog_item_id: null, child_expr: typeStandards[0]?.key ?? 'door_type_a', count_expr: '0', input_map: {}, sort: children.rows.length + 1 })}>
              + Child
            </Btn>
          )}
        </>
      }
    >
      {children.rows.length === 0 && <Empty>It creates nothing on its own.</Empty>}
      {children.rows.map((k) => {
        const map = (k.input_map ?? {}) as Record<string, string>;
        const byFormula = !k.child_catalog_item_id;
        const childCat = k.child_catalog_item_id ? lib?.catalog[k.child_catalog_item_id] : undefined;
        const title = k.label || childCat?.name || 'Child';
        return (
          <div key={k.id} {...dragChildren.row(k.id, 'dragrow')}>
            <Card
              itemId={id}
              k={`child.${k.id}`}
              className="sub"
              label={
                <>
                  {title} <small>· {byFormula ? 'a formula over the inputs decides which item' : `always ${childCat?.name ?? '…'}`} · how many: {k.count_expr}</small>
                </>
              }
              right={
                <span className="nowrap">
                  <Grip {...dragChildren.grip(k.id)} /> <Retired row={k} table="catalog_child" />{' '}
                  {canEdit && <Btn small onClick={() => window.confirm(`Are you sure you want to remove the child "${title}"? The item will no longer create it.`) && void deleteRow('catalog_child', k.id)}>×</Btn>}
                </span>
              }
            >
              <div className="row mid" style={{ gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <small>called</small>
                <Edit disabled={!canEdit} value={k.label ?? ''} placeholder="Door, Drawer front" onSave={(v) => save('catalog_child', k.id, { label: v.trim() || null })} style={{ width: 160 }} />
                <small>which item</small>
                <select
                  disabled={!canEdit}
                  value={byFormula ? 'formula' : k.child_catalog_item_id}
                  onChange={(e) =>
                    void save('catalog_child', k.id, e.target.value === 'formula' ? { child_catalog_item_id: null, child_expr: k.child_expr || (typeStandards[0]?.key ?? 'door_type_a') } : { child_catalog_item_id: e.target.value, child_expr: null })
                  }
                  style={{ maxWidth: 300 }}
                >
                  <option value="formula">A formula decides which: this item's inputs and the job's standards</option>
                  {cats.rows.filter((c) => c.id !== id).map((c) => (
                    <option key={c.id} value={c.id}>
                      Always: {c.name}
                    </option>
                  ))}
                </select>
                {byFormula && (
                  <>
                    <span className="grow" style={{ minWidth: 220 }}>
                      <Edit className="cell mono" disabled={!canEdit} value={k.child_expr ?? ''} parse={formula} onSave={(v) => save('catalog_child', k.id, { child_expr: v })} />
                    </span>
                    <Info left>
                      A formula that names the catalog item: a standard that holds one ({typeStandards.map((s) => s.key).join(', ') || 'none yet; make one under Standards with type "catalog item"'}), or a choice between them over this item's inputs:{' '}
                      <code>if(door_type = 'Type B', door_type_b, door_type_a)</code>. Each box can then override the standard for itself.
                    </Info>
                  </>
                )}
                <small>how many</small>
                <Edit className="mono" disabled={!canEdit} value={k.count_expr} parse={formula} onSave={(v) => save('catalog_child', k.id, { count_expr: v })} style={{ width: 120 }} />
              </div>
              <table className="fixed">
                <colgroup>
                  <col style={{ width: 200 }} />
                  <col />
                  <col style={{ width: 36 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Child input</th>
                    <th>Formula over this item</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(map).map(([key, expr]) => (
                    <tr key={key}>
                      <td>
                        <code>{key}</code>
                        {childCat && !childCat.inputs.some((i) => i.key === key) && <small className="warn"> not an input of {childCat.name}</small>}
                      </td>
                      <td>
                        <Edit className="cell mono" disabled={!canEdit} value={expr} parse={formulaOrBlank} onSave={(v) => { const next = { ...map }; if (v === '') delete next[key]; else next[key] = v; return save('catalog_child', k.id, { input_map: next }); }} />
                      </td>
                      <td>
                        {canEdit && (
                          <Btn small title="Remove this line" onClick={() => { const next = { ...map }; delete next[key]; void save('catalog_child', k.id, { input_map: next }); }}>
                            ×
                          </Btn>
                        )}
                      </td>
                    </tr>
                  ))}
                  {canEdit && (
                    <tr>
                      <td colSpan={3}>
                        {childCat ? (
                          <select
                            className="cell"
                            value=""
                            onChange={(e) => e.target.value && void save('catalog_child', k.id, { input_map: { ...map, [e.target.value]: '0' } })}
                          >
                            <option value="">+ fill another of {childCat.name}'s inputs…</option>
                            {childCat.inputs.filter((i) => !(i.key in map)).map((i) => (
                              <option key={i.key} value={i.key}>
                                {i.label} ({i.key})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Edit className="cell mono" value="" placeholder="+ child input key, then give it a formula" parse={snake} onSave={(v) => save('catalog_child', k.id, { input_map: { ...map, [v]: '0' } })} />
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>
        );
      })}
    </Card>
  );

  // ---------- identity ----------

  const identity = (
    <Card itemId={id} k="identity" label="Identity · the whole item" open={false}>
      <div className="row" style={{ gap: 16 }}>
        <div className="col">
          <div className="form">
            <Field label="Name" wide>
              <Edit disabled={!canEdit} value={item.name} onSave={(v) => save('catalog_item', id, { name: v || item.name })} />
            </Field>
            <Field label={<>Family<Info>The list of families is under Library → Catalog families.</Info></>}>
              <select disabled={!canEdit} value={item.family ?? ''} onChange={(e) => void save('catalog_item', id, { family: e.target.value || null })}>
                <option value="">—</option>
                {[...new Set([...families, ...(item.family ? [item.family as string] : [])])].map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={<>Mark<Info>Top-tier items get a mark on the View. Sub-items (a door, a pull) nest under their parent and never get one. A top-level line that needs no mark (a vendor order) is not asked for one by pass 1.</Info></>}>
              <select disabled={!canEdit} value={item.mark_threshold} onChange={(e) => void save('catalog_item', id, { mark_threshold: e.target.value })}>
                <option value="top_tier">Top-tier</option>
                <option value="sub_item">Sub-item</option>
                <option value="no_mark">Top-level, needs no mark</option>
              </select>
            </Field>
            <Field label={<>Recipe picked by<Info>A job standard of type "recipe" whose value names the recipe every item of this kind resolves through (Box build: Shop-built or Cabinotch). A Build flips it for the whole job; one job item can still pick another. Empty: the default recipe.</Info></>}>
              <select disabled={!canEdit} value={item.recipe_standard ?? ''} onChange={(e) => void save('catalog_item', id, { recipe_standard: e.target.value || null })}>
                <option value="">The default recipe</option>
                <optgroup label="A job standard whose value names the recipe">
                  {recipeStandards.map((s) => (
                    <option key={s.key} value={s.key}>
                      The job's {s.label}
                    </option>
                  ))}
                </optgroup>
                {derived.rows.length > 0 && (
                  <optgroup label="A calculation on this item that works the name out">
                    {derived.rows.map((d) => (
                      <option key={d.key} value={d.key}>
                        {d.label} ({d.key})
                      </option>
                    ))}
                  </optgroup>
                )}
                {item.recipe_standard && !recipeStandards.some((s) => s.key === item.recipe_standard) && !derived.rows.some((d) => d.key === item.recipe_standard) && (
                  <option value={item.recipe_standard}>{item.recipe_standard} (not a standard or a calculation here)</option>
                )}
              </select>
            </Field>
            <Field
              label={
                <>
                  Its sub lines are left out when
                  <Info>
                    A rule read on the job item, for a vendor order item that carries a sub's typed estimate (Outside finishing, CustomOne: orders = 0). When it is true under a Build, the estimate is left out of that Build's price and the job check says so; the item's own route already counts what goes. Empty: its sub lines always price (Spencer, 2026-10-17).
                  </Info>
                </>
              }
              wide
            >
              <Edit className="cell mono" disabled={!canEdit} value={item.sub_lines_off_expr ?? ''} placeholder="always priced" parse={formulaOrBlank} onSave={(v) => save('catalog_item', id, { sub_lines_off_expr: v || null })} />
            </Field>
            <Field label="Grid entry (Add many)">
              <input type="checkbox" disabled={!canEdit} checked={!!item.grid_enabled} onChange={(e) => void save('catalog_item', id, { grid_enabled: e.target.checked })} style={{ width: 20, height: 20 }} />
            </Field>
            <Field label={<>Retired (inactive)<Info>An item an old job used cannot be deleted; retire it once a new item replaces it. It stays on the jobs that have it and still prices there, and is offered to no new job. The catalog list keeps it in a Retired group at the end.</Info></>}>
              <label className="check">
                <input type="checkbox" disabled={!canEdit} checked={!!item.retired} onChange={(e) => void save('catalog_item', id, { retired: e.target.checked })} style={{ width: 20, height: 20 }} />
                {item.retired ? 'Retired · offered to no new job' : 'Active'}
              </label>
            </Field>
            <Field label={<>CAD check: compared as<Info>What a CAD export is compared against this item as. A formula over its inputs giving 'box', 'door' or 'drawer_front', in quotes. Its size is read from the inputs named width, height and depth. Leave empty for an item the CAD check should ignore.</Info></>} wide>
              <Edit disabled={!canEdit} value={item.compare_as_expr ?? ''} placeholder="Empty: not compared" onSave={(v) => save('catalog_item', id, { compare_as_expr: v || null })} />
            </Field>
            <Field label={<>Dimension roles<Info>Which input plays length, depth and width on this item, the way a material role is declared (2026-10-23). The market check per square foot reads length and depth, so every countertop checks whatever its inputs are called. Blank: the inputs keyed length, depth and width.</Info></>} wide>
              <span className="row mid" style={{ gap: 6, flexWrap: 'wrap' }}>
                {(['length', 'depth', 'width'] as const).map((role) => {
                  const roles = (item.dimension_roles ?? {}) as Record<string, string>;
                  return (
                    <label key={role} className="row mid" style={{ gap: 4 }}>
                      <small>{role}</small>
                      <select
                        disabled={!canEdit}
                        className={roles[role] ? '' : 'muted'}
                        title={roles[role] ? `Set: ${roles[role]} plays ${role}` : `Not set: the input keyed ${role} plays ${role}, if there is one`}
                        value={roles[role] ?? ''}
                        onChange={(e) => {
                          const next = { ...roles };
                          if (e.target.value) next[role] = e.target.value;
                          else delete next[role];
                          void save('catalog_item', id, { dimension_roles: next });
                        }}
                      >
                        <option value="">{inputs.rows.some((i) => i.key === role) ? `${role} (the input keyed so)` : 'none'}</option>
                        {inputs.rows.filter((i) => i.data_type === 'number' || i.data_type === 'standard').map((i) => (
                          <option key={i.id} value={i.key}>
                            {i.key}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                })}
              </span>
            </Field>
            <Field label="What the client is told it is" wide>
              <Edit disabled={!canEdit} value={item.client_sentence ?? ''} placeholder="The sentence the proposal uses" onSave={(v) => save('catalog_item', id, { client_sentence: v || null })} />
            </Field>
            <Field label="Notes" wide>
              <Edit disabled={!canEdit} value={item.notes ?? ''} onSave={(v) => save('catalog_item', id, { notes: v || null })} />
            </Field>
          </div>
          <ChecklistLines catalogItemId={id} canEdit={canEdit} />
          <div className="lb" style={{ marginTop: 10 }}>
            <span>Job types that offer it</span>
          </div>
          <div className="seg">
            {types.rows.map((t) => {
              const o = offers.rows.find((x) => x.job_type_id === t.id);
              return (
                <button key={t.id} type="button" disabled={!canEdit} className={`tag${o ? ' on' : ''}`} onClick={() => void (o ? deleteRow('job_type_catalog_item', o.id) : insertRow('job_type_catalog_item', { job_type_id: t.id, catalog_item_id: id }))}>
                  {t.name}
                </button>
              );
            })}
            {types.rows.length === 0 && <small>No job types yet.</small>}
          </div>
        </div>
        <div className="col">
          <div className="lb">
            <span>The whole item · tap a line to open it</span>
            <Info left>The item, its recipes, each recipe's route steps and materials, and the operation or material each one points to. Every line says whether it has been verified.</Info>
          </div>
          {libItem && lib ? <CatalogTree item={libItem} lib={lib} /> : <Empty>Loading the library…</Empty>}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="stack">
      <Head title={`${item.name} v${item.version}`} sub="Catalog item · the item is signed on its own (what it is: inputs, calculations, children); each recipe is signed on its own, and a job reads the recipe it uses.">
        <span className="row mid" style={{ gap: 6, flexWrap: 'wrap' }}>
          <VerifyButton table="catalog_item" row={item} />
          <small>
            {verifiedRecipes} of {recipes.rows.length} {recipes.rows.length === 1 ? 'recipe' : 'recipes'} signed
            {recipes.rows.some((r) => !r.verified) && `: not yet ${recipes.rows.filter((r) => !r.verified).map((r) => r.name).join(', ')}`}
          </small>
          {(!item.verified || recipes.rows.some((r) => !r.verified)) && (
            <Btn small read onClick={() => nav(`/verify/catalog_item/${item.id}`)} title="Everything that changed on the item and its recipes since the version you verified, old beside new, and one Verify for all of it; or Send back with a note.">
              What changed
            </Btn>
          )}
          {retiredCount > 0 && (
            <Btn small read onClick={() => setShowRetired(!showRetired)} title="Rows an AI employee retired: out of the item the engine prices, kept here with Restore (2026-10-23)">
              {showRetired ? 'Hide retired' : `Show retired · ${retiredCount}`}
            </Btn>
          )}
          {canEdit && (
            <Btn small onClick={() => void copy()} title="A copy of this item with everything on it. It lands unverified and is independent of this one from then on.">
              Copy this item
            </Btn>
          )}
          <Btn small read onClick={() => nav('/library/catalog')}>
            Back to the catalog
          </Btn>
        </span>
      </Head>

      {identity}

      {mode === 'read' && (
        <div className={`editor${railFolded ? ' folded' : ''}`}>
          <div className="rail stack">
            {railInputs}
            {railCalcs}
            {railAlso}
          </div>
          <div className="work stack">
            {recipesPanel}
            {routeCard}
            {bomCard}
            {cutListCard}
            {childrenCard}
          </div>
        </div>
      )}
      {mode === 'inputs' && (
        <div className="editor editing">
          <div className="work stack">{inputsTable}</div>
          <div className="rail stack">{railReadBy}</div>
        </div>
      )}
      {mode === 'calcs' && (
        <div className="editor editing">
          <div className="work stack">{calcsTable}</div>
          <div className="rail stack">
            {railInputs}
            {railAlso}
          </div>
        </div>
      )}

      <div className="row mid" style={{ gap: 6 }}>
        {!item.verified && <Tag kind="un">The item is unsigned: Verify above once its inputs, calculations and children are right. A recipe is signed on its own line; a job reads the recipe it uses.</Tag>}
        <span className="grow" />
        {canEdit && (
          <Btn small onClick={() => void remove()} title="Delete this catalog item and everything under it. It asks first, and refuses while a job uses it.">
            Delete this catalog item
          </Btn>
        )}
      </div>
    </div>
  );
}
